<template>
  <ContentWrap title="AI商务社交" message="AI商务社交核心运营数据概览">
    <template #header>
      <el-button size="small" type="primary" plain @click="router.push('/business/app-copy')">
        <Icon icon="ep:document" class="mr-4px" />APP配置
      </el-button>
      <el-button :loading="loading" size="small" @click="loadData">
        <Icon icon="ep:refresh" class="mr-4px" />刷新数据
      </el-button>
    </template>

    <section class="entry-section mb-20px">
      <div class="section-title section-title--large">装修AI商务社交</div>
      <el-row :gutter="20">
        <el-col v-for="entry in entries" :key="entry.name" :xs="24" :sm="24" class="mb-12px">
          <div class="entry-card" :class="entry.theme" @click="goDecorate(entry)">
            <div class="entry-card__icon"><Icon :icon="entry.icon" :size="34" color="#fff" /></div>
            <div class="entry-card__body"
              ><strong>{{ entry.name }}</strong
              ><span>{{ entry.desc }}</span
              ><small>{{ entry.tag }}</small></div
            >
            <el-button :type="entry.type" size="small" round @click.stop="goDecorate(entry)">
              去装修
            </el-button>
          </div>
        </el-col>
      </el-row>
    </section>

    <el-row :gutter="20" class="main-row">
      <el-col :xs="24" :lg="14">
        <div class="section-head">
          <div class="section-title">核心数据</div>
          <span class="section-note">实时统计</span>
        </div>
        <el-row :gutter="12" class="stats-row">
          <el-col v-for="item in statsCards" :key="item.key" :xs="12" :sm="6" class="mb-12px">
            <div class="stat-card" :class="item.theme" @click="router.push(item.path)">
              <div class="stat-card__icon"><Icon :icon="item.icon" :size="22" /></div>
              <div class="stat-card__body">
                <span>{{ item.label }}</span>
                <strong>{{ formatCount(item.value) }}</strong>
              </div>
            </div>
          </el-col>
        </el-row>

        <div class="section-head">
          <div class="section-title">业务规模</div>
          <span class="section-note">与上方同一口径</span>
        </div>
        <el-card shadow="never" class="chart-card mb-16px">
          <div ref="scaleChartRef" class="chart-box"></div>
        </el-card>
      </el-col>

      <el-col :xs="24" :lg="10">
        <div class="section-title right-title">快捷操作</div>
        <el-row :gutter="8" class="quick-row">
          <el-col
            v-for="action in quickActions"
            :key="action.name"
            :xs="12"
            :sm="6"
            :lg="6"
            class="mb-8px"
          >
            <el-card shadow="hover" class="quick-card" @click="router.push(action.path)">
              <div class="quick-card__icon" :style="{ background: action.bg, color: action.color }"
                ><Icon :icon="action.icon" :size="21"
              /></div>
              <strong>{{ action.name }}</strong
              ><span>{{ action.desc }}</span>
            </el-card>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
  </ContentWrap>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ContentWrap } from '@/components/ContentWrap'
import * as echarts from 'echarts'
import * as Api from '@/api/business/admin'

defineOptions({ name: 'BossCircleDashboard' })
const router = useRouter()
const loading = ref(false)
const data = reactive<any>({})
const scaleChartRef = ref<HTMLDivElement>()
let scaleChart: echarts.ECharts | null = null

const entries = [
  {
    name: '手机AI商务社交',
    desc: '移动端AI商务社交入口',
    tag: '响应式 · 全平台适配',
    icon: 'ep:iphone',
    type: 'warning',
    theme: 'entry-card--orange',
    // 与多业态装修一致：业态码取模块 code=business
    decoratePath: '/diy/template/list',
    decorateQuery: { industry: 'business', scene: 'home' }
  }
]

/** 概况「去装修」→ 多业态模板列表（锁定 Boss 圈业态） */
const goDecorate = (entry: (typeof entries)[number]) => {
  const path = entry.decoratePath || '/diy/template/list'
  const query = entry.decorateQuery || { industry: 'business', scene: 'home' }
  router.push({ path, query })
}

const statsCards = computed(() => [
  {
    key: 'user',
    label: '用户总数',
    value: data.userCount,
    icon: 'ep:user-filled',
    path: '/business/user-operation/user',
    theme: 'stat-card--blue'
  },
  {
    key: 'feed',
    label: '动态总数',
    value: data.feedCount,
    icon: 'ep:chat-dot-square',
    path: '/business/community/feed',
    theme: 'stat-card--green'
  },
  {
    key: 'circle',
    label: '商圈总数',
    value: data.circleCount,
    icon: 'ep:connection',
    path: '/ai-Application-center/business/circle/manage',
    theme: 'stat-card--orange'
  },
  {
    key: 'pending',
    label: '待审核动态',
    value: data.pendingFeedCount,
    icon: 'ep:warning-filled',
    path: '/business/community/feed',
    theme: 'stat-card--indigo'
  }
])

const quickActions = [
  {
    name: '用户管理',
    desc: '用户档案',
    icon: 'ep:user',
    color: '#409eff',
    bg: 'rgba(64,158,255,.1)',
    path: '/business/user-operation/user'
  },
  {
    name: '动态管理',
    desc: '内容审核',
    icon: 'ep:chat-dot-square',
    color: '#67c23a',
    bg: 'rgba(103,194,58,.1)',
    path: '/business/community/feed'
  },
  {
    name: '商圈动态',
    desc: '动态审核',
    icon: 'ep:connection',
    color: '#e6a23c',
    bg: 'rgba(230,162,60,.1)',
    path: '/ai-Application-center/business/circle/news'
  },
  {
    name: '评论管理',
    desc: '互动治理',
    icon: 'ep:comment',
    color: '#20b2aa',
    bg: 'rgba(32,178,170,.1)',
    path: '/business/community/comment'
  },
  {
    name: '资讯管理',
    desc: '资讯发布',
    icon: 'ep:reading',
    color: '#00a6c7',
    bg: 'rgba(0,166,199,.1)',
    path: '/business/news'
  },
  {
    name: '系统配置',
    desc: '模块设置',
    icon: 'ep:setting',
    color: '#607d8b',
    bg: 'rgba(96,125,139,.1)',
    path: '/business/operation/config'
  },
  {
    name: 'APP配置',
    desc: '启动页文案',
    icon: 'ep:document',
    color: '#9c27b0',
    bg: 'rgba(156,39,176,.1)',
    path: '/business/app-copy'
  }
]

const formatCount = (value?: number) => Number(value || 0).toLocaleString('zh-CN')

const scaleItems = () => [
  { name: '用户', value: Number(data.userCount || 0), color: '#409eff' },
  { name: '动态', value: Number(data.feedCount || 0), color: '#67c23a' },
  { name: '商圈', value: Number(data.circleCount || 0), color: '#e6a23c' },
  { name: '待审动态', value: Number(data.pendingFeedCount || 0), color: '#6366f1' }
]

const renderCharts = () => {
  const items = scaleItems()
  scaleChart?.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const row = Array.isArray(params) ? params[0] : params
        return `${row?.name || ''}：${formatCount(row?.value)}`
      }
    },
    grid: { top: 36, right: 16, bottom: 28, left: 48 },
    xAxis: {
      type: 'category',
      data: items.map((item) => item.name),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e6e8ee' } },
      axisLabel: { color: '#606266', fontSize: 12 }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      splitLine: { lineStyle: { type: 'dashed', color: '#eef1f6' } },
      axisLabel: { color: '#909399' }
    },
    series: [
      {
        type: 'bar',
        barWidth: 28,
        data: items.map((item) => ({
          value: item.value,
          itemStyle: {
            borderRadius: [8, 8, 0, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: item.color },
              { offset: 1, color: `${item.color}99` }
            ])
          }
        })),
        label: {
          show: true,
          position: 'top',
          color: '#303133',
          fontWeight: 700,
          formatter: (row: any) => formatCount(row.value)
        }
      }
    ]
  })
}
const loadData = async () => {
  loading.value = true
  try {
    Object.assign(data, await Api.getDashboard())
    await nextTick()
    renderCharts()
  } finally {
    loading.value = false
  }
}
const resizeCharts = () => {
  scaleChart?.resize()
}
onMounted(async () => {
  await nextTick()
  if (scaleChartRef.value) scaleChart = echarts.init(scaleChartRef.value)
  window.addEventListener('resize', resizeCharts)
  await loadData()
})
onUnmounted(() => {
  window.removeEventListener('resize', resizeCharts)
  scaleChart?.dispose()
})
</script>

<style scoped lang="scss">
.section-title {
  margin: 0;
  padding-left: 10px;
  border-left: 3px solid #409eff;
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
}
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 10px;
}
.section-note {
  color: #909399;
  font-size: 12px;
}
.section-title--large {
  margin-bottom: 16px;
  padding-left: 12px;
  border-left-width: 4px;
  color: #1a1a1a;
  font-size: 16px;
  font-weight: 700;
}
.entry-card {
  position: relative;
  display: flex;
  min-height: 128px;
  align-items: center;
  gap: 18px;
  padding: 24px;
  border: 1px solid transparent;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: 0.25s;
}
.entry-card:before {
  position: absolute;
  top: -45px;
  right: -35px;
  width: 130px;
  height: 130px;
  border-radius: 50%;
  content: '';
  opacity: 0.08;
}
.entry-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
}
.entry-card__icon {
  display: flex;
  width: 64px;
  height: 64px;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: 17px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}
.entry-card__body {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}
.entry-card__body strong {
  font-size: 18px;
}
.entry-card__body span {
  margin-top: 5px;
  color: #909399;
  font-size: 13px;
}
.entry-card__body small {
  align-self: flex-start;
  margin-top: 10px;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
}
.entry-card--green {
  border-color: rgba(103, 194, 58, 0.15);
  background: linear-gradient(180deg, #f5fdf5, #e8f7e8);
}
.entry-card--green:before,
.entry-card--green .entry-card__icon {
  background: #67c23a;
}
.entry-card--green strong {
  color: #2d6a1e;
}
.entry-card--green small {
  background: rgba(103, 194, 58, 0.12);
  color: #67c23a;
}
.entry-card--orange {
  border-color: rgba(230, 162, 60, 0.15);
  background: linear-gradient(180deg, #fffaf5, #fef3e6);
}
.entry-card--orange:before,
.entry-card--orange .entry-card__icon {
  background: #e6a23c;
}
.entry-card--orange strong {
  color: #8b5e1a;
}
.entry-card--orange small {
  background: rgba(230, 162, 60, 0.12);
  color: #e6a23c;
}
.entry-card--blue {
  border-color: rgba(64, 158, 255, 0.15);
  background: linear-gradient(180deg, #f5f9ff, #e8f2fe);
}
.entry-card--blue:before,
.entry-card--blue .entry-card__icon {
  background: #409eff;
}
.entry-card--blue strong {
  color: #1a5fa8;
}
.entry-card--blue small {
  background: rgba(64, 158, 255, 0.12);
  color: #409eff;
}
.stats-row .stat-card {
  position: relative;
  display: flex;
  min-height: 92px;
  align-items: center;
  padding: 16px 14px 16px 18px;
  overflow: hidden;
  border: 1px solid #eef1f6;
  border-radius: 14px;
  background: #fff;
  color: #303133;
  cursor: pointer;
  box-shadow: 0 8px 22px rgba(26, 24, 20, 0.05);
  transition: 0.2s;
}
.stats-row .stat-card::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  content: '';
}
.stats-row .stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(26, 24, 20, 0.1);
}
.stat-card__icon {
  display: flex;
  width: 42px;
  height: 42px;
  flex: none;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  border-radius: 12px;
}
.stat-card__body {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}
.stat-card__body span {
  margin-bottom: 4px;
  color: #8a8378;
  font-size: 12px;
}
.stat-card__body strong {
  overflow: hidden;
  color: #1a1814;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stat-card--blue::before {
  background: #409eff;
}
.stat-card--blue .stat-card__icon {
  background: rgba(64, 158, 255, 0.12);
  color: #409eff;
}
.stat-card--green::before {
  background: #67c23a;
}
.stat-card--green .stat-card__icon {
  background: rgba(103, 194, 58, 0.14);
  color: #67c23a;
}
.stat-card--orange::before {
  background: #e6a23c;
}
.stat-card--orange .stat-card__icon {
  background: rgba(230, 162, 60, 0.16);
  color: #e6a23c;
}
.stat-card--indigo::before {
  background: #6366f1;
}
.stat-card--indigo .stat-card__icon {
  background: rgba(99, 102, 241, 0.14);
  color: #6366f1;
}
.chart-card {
  border: 1px solid #eef1f6;
  border-radius: 14px;
}
.chart-card :deep(.el-card__body) {
  padding: 8px 8px 4px;
}
.chart-card .chart-box {
  width: 100%;
  height: 280px;
}
.chart-card .chart-box--small {
  height: 200px;
}
.right-title {
  margin-top: 0;
}
.quick-card {
  cursor: pointer;
  text-align: center;
  transition: 0.15s;
}
.quick-card:hover {
  transform: translateY(-2px);
}
.quick-card :deep(.el-card__body) {
  padding: 12px 4px;
}
.quick-card__icon {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  border-radius: 10px;
}
.quick-card strong,
.quick-card span {
  display: block;
}
.quick-card strong {
  color: #303133;
  font-size: 13px;
}
.quick-card span {
  margin-top: 2px;
  color: #909399;
  font-size: 11px;
}
.todo-card :deep(.el-card__body) {
  padding: 6px 18px;
}
.todo-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 2px;
  border-bottom: 1px solid #f0f2f5;
  cursor: pointer;
}
.todo-item > div {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #606266;
  font-size: 13px;
  font-weight: 500;
}
.todo-item:last-of-type {
  border-bottom: 0;
}
.mb-20px {
  margin-bottom: 20px;
}
.mb-16px {
  margin-bottom: 16px;
}
.mb-12px {
  margin-bottom: 12px;
}
.mb-8px {
  margin-bottom: 8px;
}
.mt-16px {
  margin-top: 16px;
}
@media (max-width: 1199px) {
  .right-title {
    margin-top: 20px;
  }
}
@media (max-width: 767px) {
  .entry-card {
    min-height: 108px;
    padding: 18px;
  }
  .entry-card__icon {
    width: 54px;
    height: 54px;
  }
  .entry-card__body strong {
    font-size: 16px;
  }
}
</style>
